import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API } from '@/services/api.service';
import { QUERY_KEY } from '@/shared/constants';
import {
  EMPLOYEE_COMPANY_GRAPH_QUERY,
  EMPLOYEE_STATUS_OPTIONS,
} from '@/shared/employee';
import {
  DATE_FORMATS,
  convertToObject,
  formatDateForBackend,
} from '@/utils/formUtils';
import { areAllValuesDefined } from '@/utils/utils';

/**
 * Company options and data, the annual leave calculation and the status options.
 */
export default function useEmployeeCompanyData({
  companyIdKeys,
  employeeTrackerType,
  employmentType,
  endDate,
  form,
  formData,
  isEdited,
  numberOfHolidayDaysContract,
  numberOfHolidayDaysPast,
  numberOfHolidayDaysUsed,
  refetchCompany,
  selectedCompany,
  startDate,
  t,
}) {
  const { data: companyOptionsData, isLoading: companyIsLoading } = useQuery({
    queryKey: [QUERY_KEY.companiesDropdown, refetchCompany],
    queryFn: async () => {
      return await API.companies.retrieveDropdownAllCompanies(null);
    },
  });

  useEffect(() => {
    if (
      companyOptionsData?.items &&
      companyOptionsData?.items.length > 0 &&
      companyOptionsData?.isSingleCompany
    ) {
      const [company] = companyOptionsData.items;
      const companyId = company._id;

      if (!isEdited) {
        const companyValue = convertToObject(companyIdKeys, companyId);

        form.setFieldsValue({
          ...companyValue,
        });
      }
    }
  }, [form, isEdited, companyOptionsData, companyIdKeys]);

  const companyOptions = companyOptionsData?.items?.length
    ? companyOptionsData?.items?.map((company) => ({
        value: company._id,
        label: company.name,
      }))
    : [];

  const { data: companyData, isLoading: companyDataIsLoading } = useQuery({
    queryKey: [QUERY_KEY.companiesData, selectedCompany],
    queryFn: async () =>
      await API.companies.getCompanyById(
        selectedCompany,
        EMPLOYEE_COMPANY_GRAPH_QUERY
      ),
    enabled: !!selectedCompany,
  });

  const COMPANY_DATA = useMemo(() => {
    const calculationMethods = companyData?.allowances;
    const otherAllowancesData = companyData?.otherAllowances;
    const applicationSettingsData = companyData?.applicationSettings;
    const sectorsData = companyData?.sectors;
    const bonusVariationsData = companyData?.bonusVariations;
    const suspensionVariationsData = companyData?.suspensionVariations;
    const nonMonetaryBenefitVariationsData =
      companyData?.nonMonetaryBenefitVariations;

    const calculationSettingsData = companyData?.calculationSettings;

    return {
      applicationSettings: applicationSettingsData,
      calculationSettings: calculationSettingsData,
      sectors: sectorsData
        ? sectorsData.map(({ sectorName, _id }) => ({
            value: _id,
            label: sectorName,
          }))
        : [],
      calculationMethods: calculationMethods
        ? Object.entries(calculationMethods).map(([_, val]) => val)
        : [],
      otherAllowancesData: otherAllowancesData
        ? Object.entries(otherAllowancesData)
            .filter(([key]) => key !== 'nonMonetaryBenefit')
            .map(([_, val]) => val)
        : [],
      bonusVariations: bonusVariationsData,
      suspensionVariations: suspensionVariationsData,
      nonMonetaryBenefitVariations: nonMonetaryBenefitVariationsData,
    };
  }, [companyData]);

  const hasCompanySector = COMPANY_DATA.applicationSettings?.isSector;
  const isHolidayTrackerCompany =
    COMPANY_DATA.applicationSettings?.holidayDaysTracker &&
    new Date(COMPANY_DATA.applicationSettings?.trackerStartDate).getTime() <
      Date.now();

  const isForeignWorkPermitTrackerCompany =
    !!COMPANY_DATA.applicationSettings?.foreignWorkPermitTracker;

  const numberOfAdditionalHolidayDays =
    formData?.holidayLeaveCalculation?.numberOfAdditionalHolidayDays ?? 0;

  const calculationPayload = useMemo(() => {
    return {
      startDate: formatDateForBackend(startDate, DATE_FORMATS.STANDARD),
      ...(endDate
        ? { endDate: formatDateForBackend(endDate, DATE_FORMATS.STANDARD) }
        : {}),
      employmentType: employmentType,
      employeeTrackerType: employeeTrackerType ?? 'existing',
      numberOfHolidayDaysContract,
      numberOfHolidayDaysPast: numberOfHolidayDaysPast ?? 0,
      numberOfHolidayDaysUsed: numberOfHolidayDaysUsed ?? 0,
      numberOfAdditionalHolidayDays,
    };
  }, [
    employeeTrackerType,
    employmentType,
    endDate,
    numberOfHolidayDaysContract,
    numberOfHolidayDaysPast,
    numberOfHolidayDaysUsed,
    numberOfAdditionalHolidayDays,
    startDate,
  ]);

  const { data: formulaCalculationData, isLoading: formulaLoading } = useQuery({
    queryKey: [QUERY_KEY.employeeCalculation, calculationPayload],
    queryFn: async () =>
      await API.employees.retrieveEmployeeHolidayCalculation(
        calculationPayload
      ),
    enabled: areAllValuesDefined(calculationPayload),
  });

  // An existing employee's balance is the STORED one (aligned by hand at the
  // client); the formula is only for a new employee. The form shows the
  // stored balance, moved by exactly what the user edits in the holiday
  // columns — the same rule the server applies on save.
  const storedCalculation = isEdited ? formData?.holidayLeaveCalculation : null;
  const hasStoredBalance =
    storedCalculation?.numberOfHolidayDays !== null &&
    storedCalculation?.numberOfHolidayDays !== undefined;

  const storedCalculationPayload = useMemo(
    () => ({
      ...calculationPayload,
      numberOfHolidayDaysContract:
        storedCalculation?.numberOfHolidayDaysContract ?? 0,
      numberOfHolidayDaysPast: storedCalculation?.numberOfHolidayDaysPast ?? 0,
      numberOfHolidayDaysUsed: storedCalculation?.numberOfHolidayDaysUsed ?? 0,
    }),
    [calculationPayload, storedCalculation]
  );

  // Same inputs as above when nothing was edited ⇒ same key, one request.
  const { data: storedFormulaData, isLoading: storedFormulaLoading } = useQuery(
    {
      queryKey: [QUERY_KEY.employeeCalculation, storedCalculationPayload],
      queryFn: async () =>
        await API.employees.retrieveEmployeeHolidayCalculation(
          storedCalculationPayload
        ),
      enabled:
        hasStoredBalance && areAllValuesDefined(storedCalculationPayload),
    }
  );

  const calculationData = useMemo(() => {
    const calc = formulaCalculationData?.calculation;
    const base = storedFormulaData?.calculation;
    if (!hasStoredBalance || !calc || !base) return formulaCalculationData;

    const num = (v) => Number(v) || 0;
    const delta = num(calc.numberOfHolidayDays) - num(base.numberOfHolidayDays);
    const byEnd =
      calc.numberOfHolidayDaysByEndOfContract == null
        ? null
        : num(
            storedCalculation.numberOfHolidayDaysByEndOfContract ??
              storedCalculation.numberOfHolidayDays
          ) +
          num(calc.numberOfHolidayDaysByEndOfContract) -
          num(
            base.numberOfHolidayDaysByEndOfContract ?? base.numberOfHolidayDays
          );

    return {
      ...formulaCalculationData,
      calculation: {
        ...calc,
        numberOfHolidayDays: num(storedCalculation.numberOfHolidayDays) + delta,
        numberOfHolidayDaysByEndOfContract: byEnd,
        holidayDaysYear:
          storedCalculation.holidayDaysYear ?? calc.holidayDaysYear,
        // A balance opened ahead of the calendar: until that year starts only
        // the old days can be taken.
        ...(num(storedCalculation.holidayDaysYear) > new Date().getFullYear() && {
          currentlyAvailableHolidayDays: num(
            storedCalculation.numberOfHolidayDaysPast
          ),
          numberOfHolidayDaysPast: num(
            storedCalculation.numberOfHolidayDaysPast
          ),
        }),
      },
    };
  }, [
    formulaCalculationData,
    storedFormulaData,
    hasStoredBalance,
    storedCalculation,
  ]);
  const calculationLoading =
    formulaLoading || (hasStoredBalance && storedFormulaLoading);

  const statusOptions = useMemo(() => {
    const options = EMPLOYEE_STATUS_OPTIONS.map(({ key, value, disabled }) => {
      return {
        value,
        label: t(key, { ns: 'forms' }),
        disabled,
      };
    });

    return options.filter((status) =>
      !isEdited ? status.value !== 'DELETED' : status
    );
  }, [isEdited, t]);

  return {
    COMPANY_DATA,
    calculationData,
    calculationLoading,
    companyData,
    companyDataIsLoading,
    companyIsLoading,
    companyOptions,
    companyOptionsData,
    hasCompanySector,
    isForeignWorkPermitTrackerCompany,
    isHolidayTrackerCompany,
    numberOfAdditionalHolidayDays,
    statusOptions,
  };
}
